import { Cpu, Compass, CheckCircle, XCircle } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const pillars = [
  {
    icon: Cpu,
    title: "Tecnologia Proprietária",
    tagline: "Automação que elimina o erro humano.",
    description:
      "Desenvolvemos tecnologia própria que automatiza as etapas repetitivas do processo. O que antes levava dias, agora roda em minutos — com rastreabilidade total e zero retrabalho.",
  },
  {
    icon: Compass,
    title: "Domínio do Processo",
    tagline: "Conhecemos cada etapa por dentro.",
    description:
      "Já enfrentamos todas as travas que existem no processo do AFRMM. Quando surge um problema — e vai surgir — sabemos exatamente o que fazer e a quem recorrer. Não é tentativa e erro. É experiência acumulada.",
  },
];

const comparison = [
  { aspect: "Processamento", without: "+1 dia por viagem", with: "Minutos" },
  { aspect: "Erros manuais", without: "Constantes e invisíveis", with: "Praticamente zero" },
  { aspect: "Visibilidade", without: "Planilhas e e-mails", with: "Dashboard em tempo real" },
  { aspect: "Travas no processo", without: "Espera, tentativa e erro", with: "Experiência para resolver rápido" },
];

const Solution = () => {
  const sectionRef = useScrollReveal();

  return (
    <section
      id="nossa-solucao"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-24 relative overflow-hidden bg-grid"
      style={{ background: "var(--color-carvao)" }}
    >
      <div className="container mx-auto px-4 lg:px-6 relative">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-16" data-animate>
          <div className="eyebrow justify-center" style={{ color: "var(--color-cinza-ancora)" }}>
            Nossa Diferença
          </div>
          <h2
            className="heading-h2 mb-4"
            style={{ color: "var(--color-branco-vela)" }}
          >
            A SIATECH não é uma consultoria comum.
          </h2>
          <div
            className="w-14 h-px mx-auto mb-6"
            style={{ background: "var(--color-cinza-ancora)", opacity: 0.3 }}
          />
          <p
            className="text-base font-light leading-relaxed"
            style={{ color: "var(--color-cinza-nevoa)" }}
          >
            Combinamos experiência real no processo com tecnologia proprietária
            para entregar o que ninguém mais entrega.
          </p>
        </div>

        {/* Pillar Cards */}
        <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto mb-20">
          {pillars.map((pillar, index) => (
            <div
              key={index}
              data-animate-from={index === 0 ? "left" : "right"}
              data-animate-delay={String(index + 1)}
              className="card-dark p-8"
            >
              <div
                className="w-10 h-10 rounded flex items-center justify-center mb-5"
                style={{
                  background: "rgba(242, 240, 236, 0.06)",
                  border: "1px solid rgba(176, 175, 172, 0.15)",
                }}
              >
                <pillar.icon
                  className="w-5 h-5"
                  style={{ color: "var(--color-cinza-nevoa)" }}
                />
              </div>
              <h3
                className="font-bold text-lg mb-1 tracking-tight"
                style={{ color: "var(--color-branco-vela)" }}
              >
                {pillar.title}
              </h3>
              <p
                className="italic text-sm mb-4 font-light"
                style={{ color: "var(--color-cinza-ancora)" }}
              >
                {pillar.tagline}
              </p>
              <p
                className="text-[14px] leading-relaxed font-light"
                style={{ color: "var(--color-cinza-ancora)", lineHeight: 1.65 }}
              >
                {pillar.description}
              </p>
            </div>
          ))}
        </div>

        {/* Comparison — mobile: cards / desktop: tabela */}
        <div className="max-w-3xl mx-auto" data-animate>

          {/* Mobile cards (< md) */}
          <div className="md:hidden space-y-2">
            {/* Cabeçalho colunas */}
            <div className="grid grid-cols-2 gap-2 px-1 pb-1">
              <div
                className="text-xs font-medium text-center"
                style={{ color: "var(--color-cinza-ancora)" }}
              >
                Sem a SIATECH
              </div>
              <div
                className="text-xs font-semibold text-center flex items-center justify-center gap-1"
                style={{ color: "var(--color-branco-vela)" }}
              >
                Com a SIATECH
                <CheckCircle className="w-3 h-3" style={{ color: "#22C55E" }} />
              </div>
            </div>
            {comparison.map((row, index) => (
              <div
                key={index}
                className="rounded p-3"
                style={{
                  background: "rgba(10, 10, 10, 0.4)",
                  border: "1px solid rgba(176, 175, 172, 0.10)",
                }}
              >
                <div
                  className="text-[11px] font-semibold uppercase tracking-wide mb-2"
                  style={{ color: "var(--color-cinza-ancora)" }}
                >
                  {row.aspect}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-start gap-1.5">
                    <XCircle
                      className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 opacity-50"
                      style={{ color: "#ef4444" }}
                    />
                    <span
                      className="text-xs font-light leading-snug"
                      style={{ color: "var(--color-cinza-ancora)" }}
                    >
                      {row.without}
                    </span>
                  </div>
                  <div className="flex items-start gap-1.5">
                    <CheckCircle
                      className="w-3.5 h-3.5 flex-shrink-0 mt-0.5"
                      style={{ color: "#22C55E" }}
                    />
                    <span
                      className="text-xs font-light leading-snug"
                      style={{ color: "var(--color-branco-vela)" }}
                    >
                      {row.with}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop tabela (md+) */}
          <div
            className="hidden md:block rounded overflow-hidden"
            style={{ border: "1px solid rgba(176, 175, 172, 0.15)" }}
          >
            {/* Header */}
            <div
              className="grid grid-cols-3"
              style={{ background: "rgba(10, 10, 10, 0.4)" }}
            >
              <div className="p-4" />
              <div
                className="p-4 text-sm font-medium text-center"
                style={{
                  color: "var(--color-cinza-ancora)",
                  borderLeft: "1px solid rgba(176, 175, 172, 0.12)",
                }}
              >
                Sem a SIATECH
              </div>
              <div
                className="p-4 text-sm font-semibold text-center flex items-center justify-center gap-1.5"
                style={{
                  color: "var(--color-branco-vela)",
                  borderLeft: "1px solid rgba(176, 175, 172, 0.12)",
                }}
              >
                Com a SIATECH
                <CheckCircle className="w-3.5 h-3.5" style={{ color: "#22C55E" }} />
              </div>
            </div>
            {/* Rows */}
            {comparison.map((row, index) => (
              <div
                key={index}
                className="grid grid-cols-3"
                style={{ borderTop: "1px solid rgba(176, 175, 172, 0.08)" }}
              >
                <div
                  className="p-4 text-sm font-medium"
                  style={{ color: "var(--color-branco-vela)" }}
                >
                  {row.aspect}
                </div>
                <div
                  className="p-4 text-sm text-center flex items-center justify-center gap-2"
                  style={{
                    color: "var(--color-cinza-ancora)",
                    borderLeft: "1px solid rgba(176, 175, 172, 0.08)",
                  }}
                >
                  <XCircle className="w-3.5 h-3.5 flex-shrink-0 opacity-50" style={{ color: "#ef4444" }} />
                  {row.without}
                </div>
                <div
                  className="p-4 text-sm text-center flex items-center justify-center gap-2"
                  style={{
                    color: "var(--color-branco-vela)",
                    borderLeft: "1px solid rgba(176, 175, 172, 0.08)",
                  }}
                >
                  <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#22C55E" }} />
                  {row.with}
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

export default Solution;
